import React, { useEffect, useState, useRef } from "react";
import ArticleBlock from "./ArticleBlock";
import toast from "react-hot-toast";
import { useGroq } from "../../lib/sanity";
import { imageIdToURL } from "../../utils/LinkUtils";

const GET_ARTICLES_QUERY = `{
  "article": *[_type == "article"] | order(publishDate desc)[0...500]{
    title,
    subtitle,
    "publish_date": string::split(publishDate, "T")[0],
    "slug": slug.current,
    sort,
    photo,
    "tags": tags[]->{text, color, sort},
    "authors": authors->{ "first_name": firstName, "last_name": lastName, avatar, "id": _id }
  }
}`;

function BlogList() {
  const PER_PAGE = 9;
  const [page, setPage] = useState(0);

  const { loading, error, data } = useGroq<any>(GET_ARTICLES_QUERY);

  useEffect(() => {
    return () => {
      toast.dismiss();
    };
  }, []);

  if (error) {
    toast.error(
      "Failed to establish a connection with the server. Please try again later.",
      { id: "noserverconnection", duration: Infinity }
    );
  }

  const articles: any[] = data?.article || [];
  const pageCount = Math.ceil(articles.length / PER_PAGE);
  const latestRef = useRef<HTMLHeadingElement>(null);
  const goTo = (p: number) => {
    setPage(p);
    const el = latestRef.current;
    if (el) {
      // scroll the "Latest" header to just below the fixed site header, not all
      // the way up (the featured/latest article above isn't paginated). Instant
      // scroll — `behavior:"smooth"` is a no-op on this page.
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo(0, Math.max(0, y));
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <section className="relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="pb-12 md:pb-20">
          <div className="lg:flex lg:justify-between">
            {/* Main content */}
            <div
              className="lg:flex-grow"
              data-aos="fade-down"
              data-aos-delay="200"
            >
              {/* Section title */}
              <h4
                ref={latestRef}
                className="text-4xl font-header font-bold text-white pb-4 mb-8 border-b border-gray-600"
              >
                Latest
              </h4>

              {/* Articles container */}
              <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-6 md:gap-y-8 items-start">
                {!loading && !error
                  ? articles.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE).map((item: any) => {
                      return (
                        <ArticleBlock
                          title={item.title}
                          subtitle={item.subtitle}
                          link={`/news/${item.publish_date}/${item.slug}`}
                          author_name={
                            item.authors
                              ? [item.authors.first_name, item.authors.last_name].filter(Boolean).join(" ")
                              : "Brown Esports"
                          }
                          profile_link={item.authors?.id ? `/author/${item.authors.id}` : "/"}
                          date={new Date(item.publish_date).toLocaleDateString(
                            "en-us",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              timeZone: "UTC",
                            }
                          )}
                          tags={item.tags}
                          image_link={imageIdToURL(item?.photo)}
                          author_image={item.authors?.avatar}
                          key={"articlemain" + item.sort + item.slug}
                        />
                      );
                    })
                  : [...Array(6)].map((_, i) => {
                      return (
                        <div className="flex flex-col h-full" key={"skeletonarticles" + i}>
                          <figure className="relative h-0 pb-9/16 bg-gray-600 animate-pulse mb-4"></figure>
                          <figure className="h-6 w-48 bg-gray-600 animate-pulse mb-4"></figure>
                          <figure className="h-4 w-full bg-gray-600 animate-pulse mb-2"></figure>
                          <figure className="h-4 w-full bg-gray-600 animate-pulse mb-2"></figure>
                          <figure className="h-4 w-24 bg-gray-600 animate-pulse mb-2"></figure>
                        </div>
                      );
                    })}
              </div>

              {/* Pagination */}
              {!loading && !error && pageCount > 1 ? (
                <div className="flex flex-wrap justify-center items-center gap-2 mt-12 md:mt-16">
                  <button
                    className="btn-sm text-gray-300 hover:text-gray-50 bg-gray-800 disabled:opacity-40 disabled:cursor-default"
                    disabled={page === 0}
                    onClick={() => goTo(page - 1)}
                  >
                    Prev
                  </button>
                  {Array.from({ length: pageCount }, (_, p) => p)
                    .filter(
                      (p) =>
                        p === 0 || p === pageCount - 1 || Math.abs(p - page) <= 1
                    )
                    .map((p, idx, arr) => (
                      <React.Fragment key={"pg" + p}>
                        {idx > 0 && p - arr[idx - 1] > 1 ? (
                          <span className="text-gray-500 px-1">…</span>
                        ) : null}
                        <button
                          className={
                            "btn-sm w-9 justify-center " +
                            (p === page
                              ? "text-white bg-red-600"
                              : "text-gray-300 hover:text-gray-50 bg-gray-800")
                          }
                          onClick={() => goTo(p)}
                        >
                          {p + 1}
                        </button>
                      </React.Fragment>
                    ))}
                  <button
                    className="btn-sm text-gray-300 hover:text-gray-50 bg-gray-800 disabled:opacity-40 disabled:cursor-default"
                    disabled={page === pageCount - 1}
                    onClick={() => goTo(page + 1)}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BlogList;
